import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const EMPTY = { tipo: "azienda", denominazione: "", piva: "", nome: "", email: "", telefono: "", messaggio: "", privacy: false };

export const RequestForm = ({ tipo }) => {
  const empty = tipo ? { ...EMPTY, tipo } : EMPTY;
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.privacy) return toast.error("Accetta il trattamento dei dati per continuare");
    setBusy(true);
    try {
      await api.post("/account-requests", f);
      setDone(true);
      setF(empty);
    } catch (x) {
      toast.error(errMsg(x));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div data-testid="request-success" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-10 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
        <h3 className="mt-4 font-display text-xl font-bold text-slate-900">Richiesta inviata</h3>
        <p className="mt-2 text-sm text-slate-600">Ti ricontatteremo all'indirizzo email indicato per attivare il tuo account.</p>
        <Button variant="outline" className="mt-6 bg-white" data-testid="request-new-button" onClick={() => setDone(false)}>Invia un'altra richiesta</Button>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid="request-form" className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
      {!tipo && <div className="mb-6 inline-flex rounded-lg bg-slate-100 p-1">
        {[["azienda", "Sono un'azienda"], ["studio", "Sono uno studio"]].map(([k, l]) => (
          <button type="button" key={k} data-testid={`request-tipo-${k}`} onClick={() => set("tipo", k)}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${f.tipo === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            {l}
          </button>
        ))}
      </div>}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={f.tipo !== "azienda" ? "Nome dello studio" : "Ragione sociale"} className="sm:col-span-2">
          <Input data-testid="request-denominazione-input" required minLength={2} value={f.denominazione} onChange={(e) => set("denominazione", e.target.value)} />
        </Field>
        <Field label="Partita IVA"><Input data-testid="request-piva-input" maxLength={16} value={f.piva} onChange={(e) => set("piva", e.target.value)} /></Field>
        <Field label="Referente"><Input data-testid="request-nome-input" required minLength={2} value={f.nome} onChange={(e) => set("nome", e.target.value)} /></Field>
        <Field label="Email"><Input data-testid="request-email-input" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Telefono"><Input data-testid="request-telefono-input" value={f.telefono} onChange={(e) => set("telefono", e.target.value)} /></Field>
        <Field label="Messaggio (facoltativo)" className="sm:col-span-2">
          <Textarea data-testid="request-messaggio-input" rows={3} maxLength={1500} placeholder="Es. numero di fatture annue, quante aziende segui…" value={f.messaggio} onChange={(e) => set("messaggio", e.target.value)} />
        </Field>
      </div>
      <label className="mt-5 flex items-start gap-3 text-sm text-slate-600">
        <Checkbox data-testid="request-privacy-checkbox" checked={f.privacy} onCheckedChange={(v) => set("privacy", !!v)} className="mt-0.5" />
        Acconsento al trattamento dei dati inseriti per essere ricontattato in merito alla richiesta.
      </label>
      <Button data-testid="request-submit-button" disabled={busy} className="mt-6 h-11 w-full bg-sky-600 hover:bg-sky-700 sm:w-auto sm:px-8">
        <Send className="mr-2 h-4 w-4" />{busy ? "Invio in corso…" : "Richiedi un account"}
      </Button>
    </form>
  );
};
